import type {
	BaseSuggestionHandlerConfig,
	GetLocationFn,
	LocationCoordinates
} from './types';
import type { Suggestion } from '~/components/SearchSuggest/types';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { Ref } from 'vue';

/**
 * Shared utility functions for suggestion handlers
 * Follows DRY principle by extracting common logic
 */

/**
 * Close dropdown if configured (defaults to true)
 * Single Responsibility: Only handles closing logic
 */
export function closeIfNeeded(config: BaseSuggestionHandlerConfig): void {
	if (config.closeOnSelect ?? true) {
		config.onClose();
	}
}

/**
 * Get display text from a suggestion
 * Single Responsibility: Text formatting logic
 */
export function getDisplayText(
	suggestion: Suggestion,
	options: {
		separator?: string;
		includeSubtitle?: boolean;
	} = {}
): string {
	const { separator = ', ', includeSubtitle = true } = options;
	const { title, subtitle } = suggestion.result;

	if (!includeSubtitle || !subtitle) {
		return title;
	}

	return [title, subtitle].filter(Boolean).join(separator);
}

/**
 * Get formatted text for location-based suggestions (region, city, macroregion)
 * Uses dash separator for better readability
 */
export function getLocationDisplayText(suggestion: Suggestion): string {
	return getDisplayText(suggestion, { separator: ' - ' });
}

/**
 * Update search term with suggestion text
 * Returns the updated text for convenience
 */
export function updateSearchTerm(
	suggestion: Suggestion,
	searchTerm: Ref<string>,
	customText?: string
): string {
	const text = customText || getDisplayText(suggestion);
	searchTerm.value = text;
	return text;
}

/**
 * Create a SelectAddressEvent from suggestion and optional overrides
 * Single Responsibility: Event creation logic
 * Handles null to undefined conversion for coordinates
 */
export function createLocationEvent(
	suggestion: Suggestion,
	text?: string,
	overrides: Partial<SelectAddressEvent> = {}
): SelectAddressEvent {
	const { searchParams, searchType } = suggestion;

	return {
		macroregions: searchParams.macroregions || [],
		cities: searchParams.cities || [],
		regions: searchParams.regions || [],
		states: searchParams.states || ['sp'],
		lat: searchParams.lat ?? undefined,
		lng: searchParams.lng ?? undefined,
		text,
		placeId: searchParams.placeId,
		condoSlug: searchParams.condoSlug,
		code: searchParams.code,
		searchType: searchType as SelectAddressEvent['searchType'],
		macroregionUsed: suggestion.macroregionUsed,
		...overrides
	};
}

/**
 * Fetch location coordinates and update suggestion
 * Returns updated suggestion with location data
 */
export async function fetchAndUpdateLocation(
	suggestion: Suggestion,
	text: string,
	getLocation: GetLocationFn
): Promise<Suggestion> {
	const { searchParams } = suggestion;
	const { lat, lng } = await getLocation(searchParams.placeId ?? null);

	return {
		...suggestion,
		searchParams: {
			...searchParams,
			states: searchParams.states || ['sp'],
			lat: lat ?? searchParams.lat,
			lng: lng ?? searchParams.lng,
			text
		}
	};
}

/**
 * Check if suggestion is a history item
 */
export function isHistoryItem(suggestion: Suggestion): boolean {
	return (suggestion as any).type === 'research';
}

/**
 * Check if location coordinates are missing
 */
export function hasNoCoordinates(suggestion: Suggestion): boolean {
	const { lat, lng } = suggestion.searchParams;

	return lat === undefined || lat === null || lng === undefined || lng === null;
}

/**
 * Check if suggestion needs location fetching
 * Street and POI items need coordinates
 */
export function needsLocationFetch(suggestion: Suggestion): boolean {
	const { searchType, searchParams } = suggestion;
	const isLocationBased =
		searchType === 'street' || searchType === 'point_of_interest';
	return (
		isLocationBased && !!searchParams.placeId && hasNoCoordinates(suggestion)
	);
}

/**
 * Get text from search params or fallback to display value
 */
export function getTextFromParams(
	suggestion: Suggestion,
	fallbackFn: (s: Suggestion) => string
): string {
	return suggestion.searchParams?.text ?? fallbackFn(suggestion);
}
